import { Builder } from '@builder.io/react';
import { useState, useEffect } from 'react';

const VideoIframe = ({ 
  url, 
  width = '800px', 
  height = '450px', 
  mobileWidth = '100%', 
  mobileHeight = '250px', 
  title = 'Embedded Video', 
  allowFullScreen = true, 
  lazyLoad = false,
  disableMobileFullscreen = true,
  roundedCorners = true,
  cornerRadius = 'rounded-lg',
  autoplay = false,
  mute = true,
  loop = false,
  showControls = true,
  hideBranding = false
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const processUrl = (url) => {
    if (!url) return null;
    
    try {
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        let videoId;
        let params = [];
        
        // Extract video ID from different URL formats
        if (url.includes('embed')) {
          const urlObj = new URL(url);
          urlObj.searchParams.set('autoplay', autoplay ? '1' : '0');
          urlObj.searchParams.set('mute', mute ? '1' : '0');
          urlObj.searchParams.set('loop', loop ? '1' : '0');
          urlObj.searchParams.set('controls', showControls ? '1' : '0');
          if (loop) {
            const videoId = urlObj.pathname.split('/').pop();
            urlObj.searchParams.set('playlist', videoId);
          }
          if (hideBranding) urlObj.searchParams.set('modestbranding', '1');
          urlObj.searchParams.set('rel', '0'); // Don't show related videos
          urlObj.searchParams.set('showinfo', '0'); // Hide video title and uploader info
          urlObj.searchParams.set('iv_load_policy', '3'); // Disable video annotations
          urlObj.searchParams.set('cc_load_policy', '0'); // Disable closed captions by default
          return urlObj.toString();
        }
        
        if (url.includes('v=')) {
          videoId = url.split('v=')[1].split('&')[0];
        } else if (url.includes('youtu.be')) {
          videoId = url.split('/').pop().split('?')[0];
        }
        
        if (videoId) {
          // Build parameters array
          params.push(`autoplay=${autoplay ? '1' : '0'}`);
          params.push(`mute=${mute ? '1' : '0'}`);
          params.push(`loop=${loop ? '1' : '0'}`);
          params.push(`controls=${showControls ? '1' : '0'}`);
          if (loop) params.push(`playlist=${videoId}`);
          if (hideBranding) params.push('modestbranding=1');
          params.push('rel=0'); // Don't show related videos
          params.push('showinfo=0'); // Hide video title and uploader info
          params.push('iv_load_policy=3'); // Disable video annotations
          params.push('cc_load_policy=0'); // Disable closed captions by default
          
          return `https://www.youtube.com/embed/${videoId}?${params.join('&')}`;
        }
      }
      return url.startsWith('http') ? url : `https://${url}`;
    } catch {
      return url;
    }
  };

  const embedUrl = processUrl(url);
  const currentWidth = isMobile ? mobileWidth : width;
  const currentHeight = isMobile ? mobileHeight : height;
  const showFullScreen = allowFullScreen && (!isMobile || !disableMobileFullscreen);
  const radiusClass = roundedCorners ? cornerRadius : 'rounded-none';

  return (
    <div 
      className={`w-full mx-auto aspect-video overflow-hidden ${radiusClass}`}
      style={{
        maxWidth: currentWidth,
        height: currentHeight
      }}
    >
      {embedUrl ? (
        <iframe
          src={lazyLoad ? undefined : embedUrl}
          data-src={lazyLoad ? embedUrl : undefined}
          className={`w-full h-full border-none ${radiusClass}`}
          title={title}
          allowFullScreen={showFullScreen}
          loading={lazyLoad ? 'lazy' : 'eager'}
          allow={`accelerometer; ${autoplay ? 'autoplay; ' : ''}clipboard-write; encrypted-media; gyroscope; picture-in-picture${showFullScreen ? '; fullscreen' : ''}`}
        />
      ) : (
        <div className={`p-5 bg-gray-100 text-center ${radiusClass}`}>
          Please enter a valid video URL
        </div>
      )}
    </div>
  );
};

Builder.registerComponent(VideoIframe, {
  name: 'Video Iframe',
  inputs: [
    {
      name: 'url',
      type: 'string',
      required: true,
      helperText: 'YouTube URL (e.g., https://youtu.be/VIDEO_ID) or embed URL',
    },
    {
      name: 'width',
      type: 'string',
      defaultValue: '800px',
      helperText: 'Desktop width (default: 800px) - Tailwind class or exact value',
    },
    {
      name: 'height',
      type: 'string',
      defaultValue: '450px',
      helperText: 'Desktop height (default: 450px) - Tailwind class or exact value',
    },
    {
      name: 'mobileWidth',
      type: 'string',
      defaultValue: '100%',
      helperText: 'Mobile width (default: 100%) - Tailwind class or exact value',
    },
    {
      name: 'mobileHeight',
      type: 'string',
      defaultValue: '250px',
      helperText: 'Mobile height (default: 250px) - Tailwind class or exact value',
    },
    {
      name: 'title',
      type: 'string',
      defaultValue: 'Embedded Video',
    },
    {
      name: 'roundedCorners',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Enable rounded corners for the video frame',
    },
    {
      name: 'cornerRadius',
      type: 'text',
      defaultValue: 'rounded-lg',
      helperText: 'Tailwind radius class',
      enum: [
        { label: 'None', value: 'rounded-none' },
        { label: 'Small', value: 'rounded-sm' },
        { label: 'Medium', value: 'rounded-md' },
        { label: 'Large', value: 'rounded-lg' },
        { label: 'XL', value: 'rounded-xl' },
        { label: '2XL', value: 'rounded-2xl' },
        { label: 'Full', value: 'rounded-full' }
      ],
      showIf: (options) => options.get('roundedCorners') === true,
    },
    {
      name: 'allowFullScreen',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Enable fullscreen button on desktop',
    },
    {
      name: 'disableMobileFullscreen',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Disable fullscreen button on mobile devices',
    },
    {
      name: 'lazyLoad',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Lazy load the iframe for better performance',
    },
    {
      name: 'autoplay',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Autoplay the video (requires mute in most browsers)',
    },
    {
      name: 'mute',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Mute the video (required for autoplay in most browsers)',
    },
    {
      name: 'loop',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Loop the video continuously',
    },
    {
      name: 'showControls',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Show video player controls',
    },
    {
      name: 'hideBranding',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Hide YouTube branding when possible',
    }
  ],
});